/**
 * Optimized trade history store with separated positions, orders, and deals
 */

import { create } from "zustand";
import type { OrderUpdate, OpenedTrade } from "../../opened-trades/types";
import type {
  TradeHistoryBatch,
  TradeHistoryState,
  TradeHistoryActions,
  TradeOrder,
  VybeDealInternal,
  VybeOrderInternal,
  HistorySyncSource,
  HistoryBatchMode,
} from "../types";
import { dealOpenTimeMs } from "../types";

// VybeUpdateType values (align with opened-trades/types VybeUpdateType)
const UPDATE_MARKET_OPEN = 2;
const UPDATE_PENDING_OPEN = 3;
const UPDATE_MARKET_CLOSE = 4;
const UPDATE_PARTIAL_CLOSE = 5;
const UPDATE_FILLED = 7;
const UPDATE_MARKET_MODIFY = 9;
const UPDATE_PENDING_MODIFY = 10;
const UPDATE_ON_STOP_LOSS = 11;
const UPDATE_ON_TAKE_PROFIT = 12;
const UPDATE_MARKET_CLOSE_BY = 17;

function nowIso(): string {
  return new Date().toISOString();
}

function stampPosition(
  p: TradeOrder,
  source: HistorySyncSource
): TradeOrder {
  return { ...p, syncSource: source, syncedAt: nowIso() };
}

function stampOrderInternal(
  o: VybeOrderInternal,
  source: HistorySyncSource
): VybeOrderInternal {
  return { ...o, syncSource: source, syncedAt: nowIso() };
}

function stampDeal(
  d: VybeDealInternal,
  source: HistorySyncSource
): VybeDealInternal {
  return { ...d, syncSource: source, syncedAt: nowIso() };
}

function isMeaningfulDeal(d: VybeDealInternal | null | undefined): d is VybeDealInternal {
  return !!d && typeof d.ticketNumber === "number" && d.ticketNumber > 0;
}

function openedTradeToTradeOrder(t: OpenedTrade): TradeOrder {
  return {
    ...t,
    closeComment: t.closeComment ?? "",
  } as TradeOrder;
}

function isClosedPositionOrder(o: OpenedTrade | TradeOrder): boolean {
  const ct = o.closeTime;
  if (!ct || typeof ct !== "string") return false;
  if (ct.startsWith("0001-01-01")) return false;
  const ts = o.closeTimestampUTC;
  if (typeof ts === "number" && ts > 0) return true;
  const t = Date.parse(ct);
  return Number.isFinite(t) && t > 0;
}

// Date filter types
export type DateFilter =
  | "today"
  | "lastWeek"
  | "lastMonth"
  | "last3months"
  | "last6months"
  | "lastYear"
  | "custom"
  /** Chart overlays / callers that need unfiltered history */
  | "all";

// Export store type for external usage
export type TradeHistoryStore = TradeHistoryStoreType;

interface TradeHistoryStoreType
  extends Omit<TradeHistoryState, "allTrades">,
    TradeHistoryActions {
  // Replace allTrades with separated arrays
  allPositions: TradeOrder[]; // From orders array
  allOrders: VybeOrderInternal[]; // From internals array (pending orders)
  allDeals: VybeDealInternal[]; // From deals array

  // Additional optimized data structures
  uniqueSymbols: Set<string>;
  positionsByTicket: Map<number, TradeOrder>;
  ordersByTicket: Map<number, VybeOrderInternal>;
  dealsByTicket: Map<number, VybeDealInternal>;

  // Pre-computed date boundaries for performance
  todayStart: number;
  weekAgo: number;
  monthAgo: number;
  threeMonthsAgo: number;
  sixMonthsAgo: number;
  oneYearAgo: number;

  // New getters
  getUniqueSymbols: () => string[];

  // Add individual items
  addPosition: (position: TradeOrder) => void;
  addOrder: (order: VybeOrderInternal) => void;
  addDeal: (deal: VybeDealInternal) => void;

  /** Merge VybeOnOrderUpdate into history maps (stamped realtime). */
  applyRealtimeOrderUpdate: (data: OrderUpdate) => void;
}

// Helper to get start of day in milliseconds
const getStartOfDay = (date: Date): number => {
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  return d.getTime();
};

// Helper to calculate date boundaries
const calculateDateBoundaries = () => {
  const now = new Date();
  const todayStart = getStartOfDay(now);

  const weekDate = new Date(now);
  weekDate.setDate(weekDate.getDate() - 7);

  const monthDate = new Date(now);
  monthDate.setMonth(monthDate.getMonth() - 1);

  const threeMonthsDate = new Date(now);
  threeMonthsDate.setMonth(threeMonthsDate.getMonth() - 3);

  const sixMonthsDate = new Date(now);
  sixMonthsDate.setMonth(sixMonthsDate.getMonth() - 6);

  const yearDate = new Date(now);
  yearDate.setFullYear(yearDate.getFullYear() - 1);

  const boundaries = {
    todayStart,
    weekAgo: getStartOfDay(weekDate),
    monthAgo: getStartOfDay(monthDate),
    threeMonthsAgo: getStartOfDay(threeMonthsDate),
    sixMonthsAgo: getStartOfDay(sixMonthsDate),
    oneYearAgo: getStartOfDay(yearDate),
  };

  return boundaries;
};

const initialState: Omit<TradeHistoryState, "allTrades"> = {
  byTicket: new Map(),
  bySymbol: new Map(),
  loadingStatus: "idle",
  errorMessage: null,
  totalExpected: 0,
  totalLoaded: 0,
  oldestTradeDate: null,
  newestTradeDate: null,
};

function rebuildDerived(
  newPositionsByTicket: Map<number, TradeOrder>,
  newOrdersByTicket: Map<number, VybeOrderInternal>,
  newDealsByTicket: Map<number, VybeDealInternal>,
  newUniqueSymbols: Set<string>,
  oldestDate: string | null,
  newestDate: string | null
) {
  const newBySymbol = new Map<string, TradeOrder[]>();
  const newAllPositions = Array.from(newPositionsByTicket.values()).sort(
    (a, b) =>
      new Date(b.closeTime).getTime() - new Date(a.closeTime).getTime()
  );

  const newAllOrders = Array.from(newOrdersByTicket.values()).sort(
    (a, b) => b.openTimeMs - a.openTimeMs
  );

  const newAllDeals = Array.from(newDealsByTicket.values()).sort(
    (a, b) => dealOpenTimeMs(b) - dealOpenTimeMs(a)
  );

  for (const position of newAllPositions) {
    if (!newBySymbol.has(position.symbol)) {
      newBySymbol.set(position.symbol, []);
    }
    newBySymbol.get(position.symbol)!.push(position);
  }

  const totalLoaded =
    newPositionsByTicket.size +
    newOrdersByTicket.size +
    newDealsByTicket.size;

  return {
    byTicket: newPositionsByTicket,
    bySymbol: newBySymbol,
    positionsByTicket: newPositionsByTicket,
    ordersByTicket: newOrdersByTicket,
    dealsByTicket: newDealsByTicket,
    uniqueSymbols: newUniqueSymbols,
    totalLoaded,
    oldestTradeDate: oldestDate,
    newestTradeDate: newestDate,
    allPositions: newAllPositions,
    allOrders: newAllOrders,
    allDeals: newAllDeals,
  };
}

export const useTradeHistoryStore = create<TradeHistoryStoreType>(
  (set, get) => {
    const dateBoundaries = calculateDateBoundaries();

    return {
      ...initialState,
      allPositions: [],
      allOrders: [],
      allDeals: [],
      uniqueSymbols: new Set(),
      positionsByTicket: new Map(),
      ordersByTicket: new Map(),
      dealsByTicket: new Map(),
      ...dateBoundaries,

      processBatch: (batch: TradeHistoryBatch) => {
        const extendedBatch = batch;

        set((state) => {
          if (!extendedBatch.success) {
            return {
              ...state,
              loadingStatus: "error" as const,
              errorMessage: extendedBatch.errorMessage ?? "Unknown error",
            };
          }

          const source = extendedBatch.historySource ?? null;
          const pendingOnly = source === "Pending";
          const closedBucket =
            source === "Closed" || source === null || source === undefined;

          const syncSource: HistorySyncSource =
            extendedBatch.syncSource ?? "server-reload";
          const batchMode: HistoryBatchMode =
            extendedBatch.batchMode ?? "authoritative";

          if (state.loadingStatus === "idle") {
            const newState = {
              ...state,
              loadingStatus: "loading" as const,
            };
            Object.assign(state, newState);
          }

          const newPositionsByTicket = new Map(state.positionsByTicket);
          const newOrdersByTicket = new Map(state.ordersByTicket);
          const newDealsByTicket = new Map(state.dealsByTicket);
          const newUniqueSymbols = new Set(state.uniqueSymbols);

          let oldestDate = state.oldestTradeDate;
          let newestDate = state.newestTradeDate;

          // Authoritative: drop client-only realtime rows missing from server snapshot
          if (batchMode === "authoritative") {
            if (pendingOnly) {
              const serverTickets = new Set(
                (extendedBatch.internals ?? []).map((o) => o.ticketNumber)
              );
              for (const [ticket, ord] of newOrdersByTicket) {
                if (ord.syncSource === "realtime" && !serverTickets.has(ticket)) {
                  newOrdersByTicket.delete(ticket);
                }
              }
            } else if (closedBucket) {
              const serverPosTickets = new Set(
                (extendedBatch.orders ?? []).map((o) => o.ticket)
              );
              for (const [ticket, pos] of newPositionsByTicket) {
                if (
                  pos.syncSource === "realtime" &&
                  !serverPosTickets.has(ticket)
                ) {
                  newPositionsByTicket.delete(ticket);
                }
              }
              const serverDealTickets = new Set(
                (extendedBatch.deals ?? []).map((d) => d.ticketNumber)
              );
              for (const [ticket, deal] of newDealsByTicket) {
                if (
                  deal.syncSource === "realtime" &&
                  !serverDealTickets.has(ticket)
                ) {
                  newDealsByTicket.delete(ticket);
                }
              }
            }
          }

          if (!pendingOnly && extendedBatch.orders?.length) {
            for (const position of extendedBatch.orders) {
              const stamped = stampPosition(position, syncSource);
              newPositionsByTicket.set(stamped.ticket, stamped);

              if (stamped.symbol && stamped.symbol.trim() !== "") {
                newUniqueSymbols.add(stamped.symbol);
              }

              const closeTime = stamped.closeTime;
              if (!oldestDate || closeTime < oldestDate) {
                oldestDate = closeTime;
              }
              if (!newestDate || closeTime > newestDate) {
                newestDate = closeTime;
              }
            }
          }

          if (extendedBatch.internals?.length) {
            for (const order of extendedBatch.internals) {
              const stamped = stampOrderInternal(order, syncSource);
              newOrdersByTicket.set(stamped.ticketNumber, stamped);

              if (stamped.symbol && stamped.symbol.trim() !== "") {
                newUniqueSymbols.add(stamped.symbol);
              }
            }
          }

          if (!pendingOnly && extendedBatch.deals?.length) {
            for (const deal of extendedBatch.deals) {
              const stamped = stampDeal(deal, syncSource);
              newDealsByTicket.set(stamped.ticketNumber, stamped);
              if (stamped.symbol && stamped.symbol.trim() !== "") {
                newUniqueSymbols.add(stamped.symbol);
              }

              const openTimeStr = new Date(dealOpenTimeMs(stamped)).toISOString();

              if (!oldestDate || openTimeStr < oldestDate) {
                oldestDate = openTimeStr;
              }
              if (!newestDate || openTimeStr > newestDate) {
                newestDate = openTimeStr;
              }
            }
          }

          const derived = rebuildDerived(
            newPositionsByTicket,
            newOrdersByTicket,
            newDealsByTicket,
            newUniqueSymbols,
            oldestDate,
            newestDate
          );

          return {
            ...state,
            ...derived,
            loadingStatus: "complete" as const,
            errorMessage: null,
          };
        });
      },

      applyRealtimeOrderUpdate: (data: OrderUpdate) => {
        set((state) => {
          const newPositionsByTicket = new Map(state.positionsByTicket);
          const newOrdersByTicket = new Map(state.ordersByTicket);
          const newDealsByTicket = new Map(state.dealsByTicket);
          const newUniqueSymbols = new Set(state.uniqueSymbols);
          let oldestDate = state.oldestTradeDate;
          let newestDate = state.newestTradeDate;

          const src: HistorySyncSource = "realtime";
          const affectedTickets = new Set<number>();
          const orderTicket = data.order?.ticket ?? 0;
          const dealPositionTicket = data.deal?.positionTicket ?? 0;
          const oppositeDealPositionTicket =
            data.oppositeDeal?.positionTicket ?? 0;

          if (orderTicket > 0) {
            affectedTickets.add(orderTicket);
          }
          if (data.closeByTicket > 0) {
            affectedTickets.add(data.closeByTicket);
          }
          if (dealPositionTicket > 0) {
            affectedTickets.add(dealPositionTicket);
          }
          if (oppositeDealPositionTicket > 0) {
            affectedTickets.add(oppositeDealPositionTicket);
          }

          if (isMeaningfulDeal(data.deal)) {
            const d = stampDeal(data.deal, src);
            newDealsByTicket.set(d.ticketNumber, d);
            if (d.symbol?.trim()) newUniqueSymbols.add(d.symbol);
            const openTimeStr = new Date(dealOpenTimeMs(d)).toISOString();
            if (!oldestDate || openTimeStr < oldestDate) oldestDate = openTimeStr;
            if (!newestDate || openTimeStr > newestDate) newestDate = openTimeStr;
          }
          if (
            isMeaningfulDeal(data.oppositeDeal) &&
            (!isMeaningfulDeal(data.deal) ||
              data.oppositeDeal.ticketNumber !== data.deal.ticketNumber)
          ) {
            const d = stampDeal(data.oppositeDeal, src);
            newDealsByTicket.set(d.ticketNumber, d);
            if (d.symbol?.trim()) newUniqueSymbols.add(d.symbol);
            const openTimeStr = new Date(dealOpenTimeMs(d)).toISOString();
            if (!oldestDate || openTimeStr < oldestDate) oldestDate = openTimeStr;
            if (!newestDate || openTimeStr > newestDate) newestDate = openTimeStr;
          }

          if (data.orderInternal && data.orderInternal.ticketNumber > 0) {
            const o = stampOrderInternal(data.orderInternal, src);
            newOrdersByTicket.set(o.ticketNumber, o);
            if (o.symbol?.trim()) newUniqueSymbols.add(o.symbol);
          }

          const closedTypes = new Set([
            UPDATE_MARKET_CLOSE,
            UPDATE_ON_STOP_LOSS,
            UPDATE_ON_TAKE_PROFIT,
            UPDATE_PARTIAL_CLOSE,
            UPDATE_MARKET_CLOSE_BY,
          ]);

          if (data.order) {
            const ot = data.type;
            if (closedTypes.has(ot) || (ot === UPDATE_FILLED && isClosedPositionOrder(data.order))) {
              const pos = stampPosition(openedTradeToTradeOrder(data.order), src);
              newPositionsByTicket.set(pos.ticket, pos);
              if (pos.symbol?.trim()) newUniqueSymbols.add(pos.symbol);
              const closeTime = pos.closeTime;
              if (!oldestDate || closeTime < oldestDate) oldestDate = closeTime;
              if (!newestDate || closeTime > newestDate) newestDate = closeTime;
              affectedTickets.add(pos.ticket);
            } else if (
              ot === UPDATE_MARKET_MODIFY ||
              ot === UPDATE_PENDING_MODIFY
            ) {
              // Pending / stops change: refresh pending row if present
              if (data.orderInternal?.ticketNumber) {
                const o = stampOrderInternal(data.orderInternal, src);
                newOrdersByTicket.set(o.ticketNumber, o);
                if (o.symbol?.trim()) newUniqueSymbols.add(o.symbol);
              }
            } else if (ot === UPDATE_PENDING_OPEN || ot === UPDATE_MARKET_OPEN) {
              // New pending: prefer orderInternal; open market position is not in closed history
              if (data.orderInternal?.ticketNumber) {
                const o = stampOrderInternal(data.orderInternal, src);
                newOrdersByTicket.set(o.ticketNumber, o);
                if (o.symbol?.trim()) newUniqueSymbols.add(o.symbol);
              }
            }
          }

          affectedTickets.forEach((ticket) => {
            newOrdersByTicket.delete(ticket);
          });

          const derived = rebuildDerived(
            newPositionsByTicket,
            newOrdersByTicket,
            newDealsByTicket,
            newUniqueSymbols,
            oldestDate,
            newestDate
          );

          return {
            ...state,
            ...derived,
            loadingStatus:
              state.loadingStatus === "idle" ? "complete" : state.loadingStatus,
          };
        });
      },

      reset: () => {
        set({
          ...initialState,
          byTicket: new Map(),
          bySymbol: new Map(),
          allPositions: [],
          allOrders: [],
          allDeals: [],
          uniqueSymbols: new Set(),
          positionsByTicket: new Map(),
          ordersByTicket: new Map(),
          dealsByTicket: new Map(),
          ...calculateDateBoundaries(),
        });
      },

      requestMoreData: (fromDate: string, toDate: string) => {
        import("../../connection")
          .then(({ VybeTraderClient }) => {
            const client = VybeTraderClient.getInstance();
            if (client.getConnectionStatus() === "connected") {
              client.requestAdditionalTradeHistory(fromDate, toDate);
            }
          })
          .catch((error) => {
            console.error(
              "[TradeHistory] Failed to import VybeTraderClient:",
              error
            );
          });
      },

      getUniqueSymbols: () => {
        return Array.from(get().uniqueSymbols).sort();
      },

      addPosition: (position: TradeOrder) => {
        set((state) => {
          const newPositionsByTicket = new Map(state.positionsByTicket);
          const newUniqueSymbols = new Set(state.uniqueSymbols);
          const stamped = stampPosition(position, "realtime");
          newPositionsByTicket.set(stamped.ticket, stamped);
          if (stamped.symbol?.trim()) newUniqueSymbols.add(stamped.symbol);
          let oldestDate = state.oldestTradeDate;
          let newestDate = state.newestTradeDate;
          const closeTime = stamped.closeTime;
          if (!oldestDate || closeTime < oldestDate) oldestDate = closeTime;
          if (!newestDate || closeTime > newestDate) newestDate = closeTime;
          const derived = rebuildDerived(
            newPositionsByTicket,
            new Map(state.ordersByTicket),
            new Map(state.dealsByTicket),
            newUniqueSymbols,
            oldestDate,
            newestDate
          );
          return { ...state, ...derived };
        });
      },

      addOrder: (order: VybeOrderInternal) => {
        set((state) => {
          const newOrdersByTicket = new Map(state.ordersByTicket);
          const newUniqueSymbols = new Set(state.uniqueSymbols);
          const stamped = stampOrderInternal(order, "realtime");
          newOrdersByTicket.set(stamped.ticketNumber, stamped);
          if (stamped.symbol?.trim()) newUniqueSymbols.add(stamped.symbol);
          const derived = rebuildDerived(
            new Map(state.positionsByTicket),
            newOrdersByTicket,
            new Map(state.dealsByTicket),
            newUniqueSymbols,
            state.oldestTradeDate,
            state.newestTradeDate
          );
          return { ...state, ...derived };
        });
      },

      addDeal: (deal: VybeDealInternal) => {
        set((state) => {
          const newDealsByTicket = new Map(state.dealsByTicket);
          const newUniqueSymbols = new Set(state.uniqueSymbols);
          const stamped = stampDeal(deal, "realtime");
          newDealsByTicket.set(stamped.ticketNumber, stamped);
          if (stamped.symbol?.trim()) newUniqueSymbols.add(stamped.symbol);
          let oldestDate = state.oldestTradeDate;
          let newestDate = state.newestTradeDate;
          const openTimeStr = new Date(dealOpenTimeMs(stamped)).toISOString();
          if (!oldestDate || openTimeStr < oldestDate) oldestDate = openTimeStr;
          if (!newestDate || openTimeStr > newestDate) newestDate = openTimeStr;
          const derived = rebuildDerived(
            new Map(state.positionsByTicket),
            new Map(state.ordersByTicket),
            newDealsByTicket,
            newUniqueSymbols,
            oldestDate,
            newestDate
          );
          return { ...state, ...derived };
        });
      },

      getTradeByTicket: (ticket: number) => get().byTicket.get(ticket),
      getTradesBySymbol: (symbol: string) => get().bySymbol.get(symbol) || [],
      isComplete: () => get().loadingStatus === "complete",
      isLoading: () => get().loadingStatus === "loading",
    };
  }
);
